import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, MapPinned, ShieldCheck, Sparkles } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { GOOGLE_MAPS_PROFILE, hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/ro";
const title = "Închirieri auto București — fără garanție, fără card credit";
const description =
  "Închirieri auto în București fără garanție și fără card de credit. Asigurare completă și rovinietă incluse, livrare gratuită în oraș și la Otopeni.";

export const Route = createFileRoute("/ro/")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ro_RO" },
      {
        name: "keywords",
        content:
          "inchirieri auto bucuresti, inchirieri masini bucuresti, rent a car bucuresti fara garantie, inchirieri auto otopeni, masini de inchiriat bucuresti",
      },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/", "/ro"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoRental",
          "@id": "https://carentals.ro/#localbusiness",
          name: "Car Rental Bucharest",
          url: CANONICAL,
          image: "https://carentals.ro/icon-512.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Bulevardul Regina Elisabeta 33",
            addressLocality: "București",
            addressCountry: "RO",
          },
          areaServed: { "@type": "City", name: "București", addressCountry: "RO" },
          priceRange: "€€",
          currenciesAccepted: "EUR, RON",
          paymentAccepted: "Card de debit, Numerar, Transfer bancar",
          sameAs: [GOOGLE_MAPS_PROFILE],
          hasMap: GOOGLE_MAPS_PROFILE,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              q: "Aveți nevoie de garanție sau card de credit?",
              a: "Nu. Închiriem fără garanție și fără card de credit — plătiți cu cardul de debit, numerar în EUR sau RON, ori prin transfer bancar. Nu blocăm nicio sumă pe card.",
            },
            {
              q: "Ce include prețul pe zi?",
              a: "Asigurare completă (RCA și CASCO), rovinieta, kilometraj nelimitat în România și asistență rutieră 24/7.",
            },
            {
              q: "Livrați mașina la Aeroportul Otopeni?",
              a: "Da, predarea la Aeroportul Henri Coandă (Otopeni) și la biroul din Bulevardul Regina Elisabeta 33 este gratuită, la orice oră. Livrarea la altă adresă din București costă 20 €.",
            },
            {
              q: "Ce acte îmi trebuie?",
              a: "Permis de conducere valabil de minimum un an și un act de identitate sau pașaport. Vârsta minimă este 21 de ani.",
            },
          ].map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: RoHome,
});

const puncte = [
  {
    icon: ShieldCheck,
    title: "Garanție 0 €",
    text: "Nu blocăm nicio sumă pe card, nici înainte, nici după închiriere.",
  },
  {
    icon: Sparkles,
    title: "Fără card de credit",
    text: "Card de debit, numerar în EUR sau RON, ori transfer bancar pentru firme.",
  },
  {
    icon: CheckCircle2,
    title: "Totul inclus",
    text: "RCA și CASCO, rovinietă, kilometraj nelimitat și asistență rutieră 24/7.",
  },
  {
    icon: MapPinned,
    title: "Livrare gratuită",
    text: "Gratuit în București și la Aeroportul Otopeni, la orice oră.",
  },
];

function RoHome() {
  

  return (
    <>
      <section className="bg-ink py-20 text-ink-foreground lg:py-28">
        <div className="container-page">
          <p className="eyebrow">Fără garanție · Fără card de credit · București</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Închirieri auto București fără garanție
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80 sm:text-lg">
            Închiriază o mașină premium sau de lux în București fără garanție și fără card de
            credit. Asigurarea completă și rovinieta sunt incluse în tarif, iar predarea în oraș și
            la Aeroportul Otopeni este gratuită. Prețuri de la 44 € pe zi.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Vezi flota</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">
                Scrie-ne pe WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <div className="max-w-3xl">
          <p className="eyebrow">De ce carentals.ro</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Închirieri mașini București, fără sume blocate pe card
          </h2>
          <p className="mt-4 text-muted-foreground">
            Majoritatea firmelor de rent a car din București blochează între 300 € și 1.500 € pe un
            card de credit și eliberează suma la săptămâni după returnare. Noi nu facem asta: nu
            cerem garanție și acceptăm card de debit, numerar sau transfer bancar.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {puncte.map((p) => (
            <div key={p.title} className="rounded-xl border border-border bg-card p-6 shadow-card">
              <p.icon className="size-6 text-gold" />
              <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild variant="gold">
            <Link to="/ro/inchirieri-auto-fara-garantie">Cum funcționează fără garanție</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/ro/inchirieri-auto-otopeni">Închirieri auto Otopeni</Link>
          </Button>
        </div>
      </section>

      <section className="bg-secondary/60 py-20">
        <div className="container-page">
          <div className="grid gap-4 sm:flex sm:items-end sm:justify-between">
            <div className="max-w-xl">
              <p className="eyebrow">Flota noastră</p>
              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Mașini disponibile azi</h2>
              <p className="mt-3 text-muted-foreground">
                28 de mașini verificate și pregătite pentru predare imediată în București.
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to="/rentals">Vezi toate mașinile</Link>
            </Button>
          </div>
          <CarGrid cars={cars.slice(0, 8)} className="mt-10" />
        </div>
      </section>

    </>
  );
}
